"use client";

import type { Decision, DecisionOption } from "@/lib/api";
import type { ReviewDecisionThread } from "@/lib/api/projection-types";
import { sanitizeSurfaceTitle } from "@/lib/savedResourceTitles";

const STATUS_META: Record<
  ReviewDecisionThread["status"],
  { label: string; tone: string }
> = {
  exploring: {
    label: "Exploring",
    tone: "border-amber/25 bg-amber/10 text-amber",
  },
  leaning: {
    label: "Leaning",
    tone: "border-blue/25 bg-blue/10 text-blue",
  },
  decided: {
    label: "Decided",
    tone: "border-forest/20 bg-sage-pale text-forest-med",
  },
  revisit: {
    label: "Revisit",
    tone: "border-border bg-bg text-muted",
  },
};

function compactSentence(text?: string | null, fallback: string = "No clear lean yet.") {
  const normalized = (text || "").replace(/\s+/g, " ").trim();
  if (!normalized) return fallback;
  const firstSentence = normalized.split(/(?<=[.!?])\s+/)[0] || normalized;
  return sanitizeSurfaceTitle(firstSentence);
}

function scoreTone(score?: number | null) {
  if (score == null) return "text-muted";
  if (score >= 8) return "text-forest-med";
  if (score >= 6) return "text-blue";
  return "text-amber";
}

function OptionChoice({
  option,
  chosen,
  disabled,
  onChoose,
}: {
  option: DecisionOption;
  chosen: boolean;
  disabled: boolean;
  onChoose: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onChoose}
      disabled={disabled}
      className={`w-full rounded-2xl border px-3.5 py-3 text-left transition-colors ${
        chosen
          ? "border-forest/25 bg-sage-pale/55"
          : "border-border bg-white hover:bg-bg"
      } disabled:cursor-not-allowed disabled:opacity-60`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-text">{sanitizeSurfaceTitle(option.name)}</p>
          {(option.judgment || option.pros.length > 0) && (
            <p className="mt-1 text-[11px] leading-relaxed text-text-light">
              {compactSentence(
                option.judgment || option.pros[0],
                "This is still a live option."
              )}
            </p>
          )}
        </div>
        {option.score != null && (
          <span className={`text-sm font-semibold ${scoreTone(option.score)}`}>
            {option.score}/10
          </span>
        )}
      </div>
      {chosen && (
        <p className="mt-2 text-[11px] font-medium text-forest-med">Chosen for now.</p>
      )}
    </button>
  );
}

interface Props {
  thread: ReviewDecisionThread;
  decision: Decision | null;
  expanded: boolean;
  busyAction: "analyze" | "choose" | "cancel" | null;
  onDecideNow: () => void;
  onCancel: () => void;
  onAnalyze: () => void;
  onChoose: (optionName: string) => void;
}

export default function DecisionThreadCard({
  thread,
  decision,
  expanded,
  busyAction,
  onDecideNow,
  onCancel,
  onAnalyze,
  onChoose,
}: Props) {
  const status = STATUS_META[thread.status];
  const threadTopic = sanitizeSurfaceTitle(thread.topic);
  const canAnalyze = Boolean(decision) && !decision?.ai_recommendation;
  const options = decision?.options ?? [];
  const primaryActionLabel =
    thread.status === "decided" ? "Change decision" : "Decide";

  return (
    <article
      data-testid="decision-thread-card"
      data-reveal-id={thread.id}
      data-reveal-ids={[thread.id, ...thread.source_decision_ids].join(" ")}
      className="rounded-[28px] border border-border bg-card p-5 shadow-sm"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <span
            data-testid="decision-thread-status"
            className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] ${status.tone}`}
          >
            {status.label}
          </span>
          <h3 className="mt-3 text-lg font-semibold text-text">{threadTopic}</h3>
        </div>
        <p className="text-[11px] text-muted">
          {thread.source_decision_ids.length} source
          {thread.source_decision_ids.length === 1 ? "" : "s"}
        </p>
      </div>

      {/* codex follow-up 2026-05-25 LIBRARY-DECISION P1 cluster:
          option names must be visible on the collapsed card so the
          user (and the strict UAT) can see the choice at a glance.
          Previously options only rendered inside the expanded panel,
          which meant the strict UAT's
          `decisionText.includes(option.name)` check failed even when
          the decision card was visible. */}
      {options.length > 0 && (
        <div
          data-testid="decision-thread-options-chips"
          className="mt-3 flex flex-wrap items-center gap-1.5"
        >
          <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted">
            Options
          </span>
          {options.map((option) => (
            <span
              key={option.name}
              data-testid="decision-thread-option-chip"
              className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11.5px] font-medium ${
                decision?.chosen_option === option.name
                  ? "border-forest-med bg-forest-med text-white"
                  : "border-border bg-bg text-text"
              }`}
            >
              {option.name}
              {typeof option.score === "number" && (
                <span className="ml-1 text-[10px] text-muted">{option.score}/10</span>
              )}
            </span>
          ))}
        </div>
      )}

      <div className="mt-4 grid gap-3 lg:grid-cols-2">
        <div className="rounded-2xl border border-border/70 bg-bg px-3.5 py-3">
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted">
            Current recommendation
          </p>
          <p className="mt-2 text-sm leading-relaxed text-text">
            {compactSentence(thread.current_recommendation, "No lean yet. Keep the comparison narrow.")}
          </p>
        </div>
        <div className="rounded-2xl border border-border/70 bg-bg px-3.5 py-3">
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted">
            Opposing consideration
          </p>
          <p className="mt-2 text-sm leading-relaxed text-text-light">
            {compactSentence(
              thread.opposing_consideration,
              "There is no explicit downside captured yet, so this decision may still need sharper tradeoffs."
            )}
          </p>
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <button
          type="button"
          onClick={onDecideNow}
          data-testid="decision-thread-decide"
          className="rounded-xl bg-forest-med px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-forest sm:flex-1"
        >
          {primaryActionLabel}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={busyAction === "cancel"}
          data-testid="decision-thread-cancel"
          className="rounded-xl border border-border px-4 py-2.5 text-sm font-medium text-text-light transition-colors hover:border-rose/25 hover:bg-rose/10 hover:text-text disabled:opacity-60 sm:flex-1"
        >
          {busyAction === "cancel" ? "Cancelling..." : "Cancel"}
        </button>
      </div>

      {expanded && (
        <div
          data-testid="decision-thread-detail"
          className="mt-4 rounded-[24px] border border-border/70 bg-white px-4 py-4"
        >
          {decision?.context && (
            <div className="rounded-2xl border border-border/70 bg-bg px-3.5 py-3">
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted">
                Context
              </p>
              <p className="mt-2 text-sm leading-relaxed text-text-light">{decision.context}</p>
            </div>
          )}

          {decision?.decision_factors.length ? (
            <div className="mt-3 flex flex-wrap gap-2">
              {decision.decision_factors.map((factor) => (
                <span
                  key={factor}
                  className="rounded-full border border-blue/15 bg-blue/8 px-2.5 py-1 text-[11px] font-medium text-blue"
                >
                  {factor}
                </span>
              ))}
            </div>
          ) : null}

          {canAnalyze && (
            <button
              type="button"
              onClick={onAnalyze}
              disabled={busyAction === "analyze"}
              data-testid="decision-thread-analyze"
              className="mt-4 rounded-xl border border-border px-4 py-2.5 text-sm font-medium text-text transition-colors hover:bg-bg disabled:opacity-60"
            >
              {busyAction === "analyze" ? "Analyzing..." : "Analyze with AI"}
            </button>
          )}

          {/* BUG_EVIDENCE_PACKET_2026-05-25 B-DECIDE-01: surface the
              full AI reasoning + delay/uncertainty guidance, not just
              the one-line summary. Both are already populated by
              decision_ai.analyze_decision but were hidden behind the
              scores-only UI. */}
          {decision?.ai_reasoning && (
            <div
              data-testid="decision-thread-reasoning"
              className="mt-4 rounded-2xl border border-forest-med/15 bg-sage-pale/30 px-3.5 py-3"
            >
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-forest-med">
                Why this leans that way
              </p>
              <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-text">
                {decision.ai_reasoning}
              </p>
            </div>
          )}

          {decision?.what_if_delay && (
            <div
              data-testid="decision-thread-what-if-delay"
              className="mt-3 rounded-2xl border border-amber/25 bg-amber/8 px-3.5 py-3"
            >
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-amber">
                What would change this recommendation
              </p>
              <p className="mt-2 text-sm leading-relaxed text-text-light">
                {decision.what_if_delay}
              </p>
            </div>
          )}

          {options.length > 0 ? (
            <div className="mt-4 space-y-3">
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted">
                Options
              </p>
              {options.map((option) => (
                <OptionChoice
                  key={option.name}
                  option={option}
                  chosen={decision?.chosen_option === option.name}
                  disabled={busyAction === "choose"}
                  onChoose={() => onChoose(option.name)}
                />
              ))}
            </div>
          ) : (
            <p className="mt-4 text-sm text-muted">
              No options are stored on this thread yet. Add sharper options before deciding.
            </p>
          )}
        </div>
      )}
    </article>
  );
}
